import { WidgetRelatedApp } from '@inno-tools/container-bridge'
import { INNOSCRIPTA_INTERNAL_ORGANIZATION_ID } from 'shared/constants/user'
import { WIDGET_NAMES, WidgetKey, WidgetSize } from 'shared/constants/widgets'
import type { User } from 'shared/types/user'
import { isProd } from 'shared/utils/isProd'

// imported from each widget's own subpath, not the top-level `../components`
// barrel — that barrel also re-exports LivechatWidgets, which pulls in a
// long chain (app-registry -> routes -> DesktopRouter -> MainPage ->
// SidebarWidgets) that leads right back to this file, creating a module-init
// cycle (verified with madge) that can leave a widget component undefined
// depending on evaluation order.
import { AiArtifactTileWidget } from '../components/AiArtifacts'
import { BudgetWidget } from '../components/BudgetWidget'
import { CSMPerformanceOverviewWidget } from '../components/CSMPerformanceOverviewWidget'
import { CalendarWidget } from '../components/CalendarWidget'
import { OnboardingWidget } from '../components/OnboardingWidget'
import { SalesGoalWidget } from '../components/SalesGoalWidget'
import { TasksWidget } from '../components/TasksWidget'
import { TimeTrackingWidget } from '../components/TimeTrackingWidget'
import { TodosWidget } from '../components/TodosWidget'
import { ClockWidget } from '../components/clock'
import { WidgetComponent } from '../types/widgets.types'

export type WidgetConfig = {
  key: WidgetKey
  name: string
  component: WidgetComponent
  relatedApp: WidgetRelatedApp
  sizes: WidgetSize[]
  isHidden?: (user: User | null) => boolean
  // hidden from marketplace but still rendered (unlike isHidden which hides from both)
  marketplaceHidden?: boolean
  hiddenAction?: 'remove' | null
}

export const WIDGETS_CONFIG: Record<WidgetKey, WidgetConfig> = {
  [WidgetKey.TIME_TRACKING]: {
    key: WidgetKey.TIME_TRACKING,
    name: WIDGET_NAMES[WidgetKey.TIME_TRACKING],
    component: TimeTrackingWidget,
    relatedApp: WidgetRelatedApp.TIME_TRACKING,
    sizes: [WidgetSize.SMALL]
  },
  [WidgetKey.CLOCK]: {
    key: WidgetKey.CLOCK,
    name: WIDGET_NAMES[WidgetKey.CLOCK],
    component: ClockWidget,
    relatedApp: WidgetRelatedApp.CALENDAR,
    sizes: [WidgetSize.SMALL, WidgetSize.MEDIUM]
  },
  [WidgetKey.GOAL_WIDGET]: {
    key: WidgetKey.GOAL_WIDGET,
    name: WIDGET_NAMES[WidgetKey.GOAL_WIDGET],
    component: SalesGoalWidget,
    relatedApp: WidgetRelatedApp.SALES,
    sizes: [WidgetSize.SMALL, WidgetSize.MEDIUM],
    isHidden: user =>
      user?.organization_id !== INNOSCRIPTA_INTERNAL_ORGANIZATION_ID,
    hiddenAction: 'remove'
  },
  [WidgetKey.BUDGET]: {
    key: WidgetKey.BUDGET,
    name: WIDGET_NAMES[WidgetKey.BUDGET],
    component: BudgetWidget,
    relatedApp: WidgetRelatedApp.HR,
    sizes: [WidgetSize.SMALL],
    isHidden: () => isProd(),
    hiddenAction: 'remove'
  },
  [WidgetKey.TASK_MANAGEMENT]: {
    key: WidgetKey.TASK_MANAGEMENT,
    name: WIDGET_NAMES[WidgetKey.TASK_MANAGEMENT],
    component: TasksWidget,
    relatedApp: WidgetRelatedApp.TASK_MANAGEMENT,
    sizes: [WidgetSize.MEDIUM]
  },
  [WidgetKey.CALENDAR]: {
    key: WidgetKey.CALENDAR,
    name: WIDGET_NAMES[WidgetKey.CALENDAR],
    component: CalendarWidget,
    relatedApp: WidgetRelatedApp.CALENDAR,
    sizes: [WidgetSize.SMALL, WidgetSize.MEDIUM, WidgetSize.LARGE]
  },
  [WidgetKey.TIPS]: {
    key: WidgetKey.TIPS,
    name: WIDGET_NAMES[WidgetKey.TIPS],
    component: OnboardingWidget,
    relatedApp: WidgetRelatedApp.ONBOARDING,
    sizes: [WidgetSize.MEDIUM],
    isHidden: () => isProd(),
    hiddenAction: 'remove'
  },
  [WidgetKey.TODOS]: {
    key: WidgetKey.TODOS,
    name: WIDGET_NAMES[WidgetKey.TODOS],
    component: TodosWidget,
    relatedApp: WidgetRelatedApp.SUBSIDIES,
    sizes: [WidgetSize.MEDIUM],
    hiddenAction: 'remove'
  },
  [WidgetKey.CSM_PERFORMANCE_OVERVIEW]: {
    key: WidgetKey.CSM_PERFORMANCE_OVERVIEW,
    name: WIDGET_NAMES[WidgetKey.CSM_PERFORMANCE_OVERVIEW],
    component: CSMPerformanceOverviewWidget,
    relatedApp: WidgetRelatedApp.AFTER_SALES,
    sizes: [WidgetSize.MEDIUM],
    isHidden: user =>
      user?.organization_id !== INNOSCRIPTA_INTERNAL_ORGANIZATION_ID,
    hiddenAction: 'remove'
  },
  [WidgetKey.AI_ARTIFACT_TILE]: {
    key: WidgetKey.AI_ARTIFACT_TILE,
    name: WIDGET_NAMES[WidgetKey.AI_ARTIFACT_TILE],
    component: AiArtifactTileWidget,
    relatedApp: WidgetRelatedApp.AI_AGENT,
    sizes: [WidgetSize.SMALL, WidgetSize.MEDIUM, WidgetSize.LARGE],
    // Added by "pin to desktop" from the gallery, not from the marketplace.
    marketplaceHidden: true
  }
}
